import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSovereignReconciliationStore, EUCountry } from "../store/sovereignReconciliationStore";
import { useAIContext } from "../store/aiContextStore";
import { useSettlementLedger } from "../store/settlementLedger";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";

/**
 * Settlement Ledger v3 - Sovereign Reconciliation
 * 
 * Country-level reconciliation of CO₂ and ESG credits between countries,
 * compliance audit trail generation, and EU export-ready JSON with eSeal signatures.
 */
export default function SovereignReconciliation() {
  const { t } = useTranslation();
  const store = useSovereignReconciliationStore();
  const ledger = useSettlementLedger();
  
  const [fromCountry, setFromCountry] = useState<EUCountry>("HU");
  const [toCountry, setToCountry] = useState<EUCountry>("DE");
  const [amount, setAmount] = useState<number>(0);
  const [scope, setScope] = useState<"SCOPE1" | "SCOPE2" | "SCOPE3" | "EMBODIED" | "ETS" | "SUBSIDY">("SCOPE1");
  const [reason, setReason] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<EUCountry | "ALL">("ALL");
  const [loading, setLoading] = useState(false);

  // Set active panel
  useEffect(() => {
    useAIContext.getState().setActivePanel("SovereignReconciliationPanel");
  }, []);

  // Update country balances from Settlement Ledger
  useEffect(() => {
    const balances = ledger.balances;
    const countryBalances: Record<EUCountry, number> = {} as any;
    
    // Extract country codes from entity names (e.g., "HU_ENTITY" -> "HU")
    Object.keys(balances).forEach((entity) => {
      const countryMatch = entity.match(/^([A-Z]{2})/);
      if (countryMatch) {
        const country = countryMatch[1] as EUCountry;
        if (!countryBalances[country]) {
          countryBalances[country] = 0;
        }
        countryBalances[country] += balances[entity];
      }
    });
    
    // Also check direct country codes
    (Object.keys(store.countryBalances) as EUCountry[]).forEach((country) => {
      if (balances[country] !== undefined) {
        countryBalances[country] = (countryBalances[country] || 0) + balances[country];
      }
    });
    
    // Update store
    if (Object.keys(countryBalances).length > 0) {
      // Note: This would require a setCountryBalances method in the store
      // For now, we'll use the reconciliations to track balances
    }
  }, [ledger.balances]);

  // Update AI context
  useEffect(() => {
    const summary = store.getReconciliationSummary();
    useAIContext.getState().setInput("reconciliationSummary", summary);
    useAIContext.getState().setInput("totalReconciliations", summary.totalReconciliations);
    useAIContext.getState().setInput("countryBalances", store.countryBalances);
  }, [store.reconciliations, store.countryBalances]);

  const summary = store.getReconciliationSummary();
  const auditTrail = store.getAuditTrail(selectedCountry === "ALL" ? undefined : selectedCountry);
  const sortedReconciliations = useMemo(() => {
    return [...store.reconciliations].sort((a, b) => b.ts - a.ts);
  }, [store.reconciliations]);

  const handleCreateReconciliation = async () => {
    if (!amount || amount <= 0 || !reason) return;
    await store.createReconciliation(
      fromCountry,
      toCountry,
      amount,
      scope,
      reason,
      (from, to, amount, reason, scope) => {
        ledger.recordTransfer(from, to, amount, reason, scope as any);
      }
    );
    setAmount(0);
    setReason("");
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const eSealHash = await store.exportReconciliationReport(
        selectedCountry === "ALL" ? undefined : selectedCountry
      );
      console.log("Exported with eSeal:", eSealHash);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = (entryId: string) => {
    store.verifyReconciliation(entryId);
  };

  const handleDispute = (entryId: string) => {
    const reason = prompt("Enter dispute reason:");
    if (reason) {
      store.disputeReconciliation(entryId, reason);
    }
  };

  // Keyboard shortcuts (after function definitions)
  useKeyboardShortcuts([
    { key: 'e', ctrl: true, action: handleExport, description: 'Export with eSeal' },
    { key: 'r', ctrl: true, action: handleCreateReconciliation, description: 'Create reconciliation' },
  ]);

  const countries: EUCountry[] = [
    "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI",
    "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MT",
    "NL", "PL", "PT", "RO", "SE", "SI", "SK",
  ];

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100 p-6">
      <div className="flex items-center justify-between border-b border-evat-gold/40 pb-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-wide text-evat-neon">
            Sovereign Reconciliation (v3)
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Országok közötti CO₂ és ESG kreditek átkönyvelése, compliance audit trail, EU export-ready JSON + eSeal
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={loading}
          className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded hover:bg-evat-neon/30 transition-colors text-sm font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Exporting..." : "Export with eSeal"}
        </button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Total Reconciliations</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">
            {summary.totalReconciliations}
          </div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Total Amount</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">
            {summary.totalAmount.toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Countries</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">
            {Object.keys(summary.byCountry).length}
          </div>
        </div>
        <div className="rounded-xl border border-evat-gold/60 bg-black/40 p-4">
          <div className="text-xs uppercase text-gray-400">Audit Entries</div>
          <div className="text-2xl font-semibold mt-1 text-evat-neon">
            {auditTrail.length}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create reconciliation */}
        <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4 space-y-4">
          <h2 className="font-semibold text-evat-neon">Create Reconciliation</h2>
          
          <div>
            <label className="text-sm text-gray-400 mb-2 block">From Country</label>
            <select
              value={fromCountry}
              onChange={(e) => setFromCountry(e.target.value as EUCountry)}
              className="w-full px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
            >
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">To Country</label>
            <select
              value={toCountry}
              onChange={(e) => setToCountry(e.target.value as EUCountry)}
              className="w-full px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
            >
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Amount</label>
            <input
              type="number"
              value={amount || ""}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
              placeholder="Enter amount"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Scope</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as typeof scope)}
              className="w-full px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
            >
              <option value="SCOPE1">SCOPE1</option>
              <option value="SCOPE2">SCOPE2</option>
              <option value="SCOPE3">SCOPE3</option>
              <option value="EMBODIED">EMBODIED</option>
              <option value="ETS">ETS</option>
              <option value="SUBSIDY">SUBSIDY</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-sm font-orbitron"
              placeholder="Enter reason"
            />
          </div>

          <button
            onClick={handleCreateReconciliation}
            disabled={!amount || amount <= 0 || !reason}
            className="w-full px-4 py-2 rounded bg-evat-neon/20 border border-evat-neon text-evat-neon hover:bg-evat-neon/30 transition-colors text-sm font-semibold font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Reconciliation
          </button>
        </div>

        {/* Country summary */}
        <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4">
          <h2 className="font-semibold text-evat-neon mb-3">Country Summary</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {Object.entries(summary.byCountry)
              .sort(([, a], [, b]) => b.net - a.net)
              .map(([country, data]) => (
                <div
                  key={country}
                  className="p-3 border border-evat-gold/30 rounded bg-black/30"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm text-evat-neon">{country}</span>
                    <span className={`text-sm font-orbitron ${
                      data.net >= 0 ? "text-evat-neon" : "text-red-400"
                    }`}>
                      Net: {data.net >= 0 ? "+" : ""}{data.net.toLocaleString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                    <div>Sent: {data.sent.toLocaleString()}</div>
                    <div>Received: {data.received.toLocaleString()}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Reconciliations table */}
      <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4">
        <h2 className="font-semibold text-evat-neon mb-3">Reconciliations</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400 border-b border-evat-gold/40">
              <tr>
                <th className="text-left py-2 pr-2">Time</th>
                <th className="text-left py-2 px-2">From</th>
                <th className="text-left py-2 px-2">To</th>
                <th className="text-right py-2 px-2">Amount</th>
                <th className="text-left py-2 px-2">Scope</th>
                <th className="text-left py-2 px-2">Reason</th>
                <th className="text-center py-2 px-2">eSeal</th>
                <th className="text-center py-2 pl-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedReconciliations.slice(0, 20).map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-black/30">
                  <td className="py-1 pr-2 whitespace-nowrap text-[11px] text-gray-400">
                    {new Date(r.ts).toLocaleString()}
                  </td>
                  <td className="py-1 px-2 font-mono text-xs text-evat-neon">{r.fromCountry}</td>
                  <td className="py-1 px-2 font-mono text-xs text-evat-neon">{r.toCountry}</td>
                  <td className="py-1 px-2 text-right text-evat-neon">
                    {r.amount.toLocaleString()}
                  </td>
                  <td className="py-1 px-2 text-gray-300">{r.scope}</td>
                  <td className="py-1 px-2 text-gray-400">{r.reason}</td>
                  <td className="py-1 px-2 text-center">
                    {r.eSealHash ? (
                      <span className="text-xs text-evat-neon" title={r.eSealHash}>
                        ✓
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">-</span>
                    )}
                  </td>
                  <td className="py-1 pl-2 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => handleVerify(r.id)}
                        className="px-2 py-1 text-xs bg-green-500/20 border border-green-500/40 text-green-400 rounded hover:bg-green-500/30"
                        title="Verify"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => handleDispute(r.id)}
                        className="px-2 py-1 text-xs bg-red-500/20 border border-red-500/40 text-red-400 rounded hover:bg-red-500/30"
                        title="Dispute"
                      >
                        ✗
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compliance audit trail */}
      <div className="rounded-xl border border-evat-gold/40 bg-black/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-evat-neon">Compliance Audit Trail</h2>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value as EUCountry | "ALL")}
            className="px-3 py-1 rounded bg-black/60 border border-evat-gold/40 text-evat-neon text-xs font-orbitron"
          >
            <option value="ALL">All Countries</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {auditTrail.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-4">No audit entries yet.</div>
          ) : (
            auditTrail.slice().reverse().map((entry) => (
              <div
                key={entry.id}
                className="p-3 border border-evat-gold/20 rounded bg-black/30"
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1">
                    <div className="text-sm font-orbitron text-evat-neon">
                      {entry.country} • {entry.action}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{entry.details}</div>
                  </div>
                  <div className="text-xs text-gray-500 ml-4">
                    {new Date(entry.ts).toLocaleString()}
                  </div>
                </div>
                {entry.verified && (
                  <div className="text-xs text-green-400 mt-1">✓ Verified</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

